import { h, Component } from 'preact';
import dlv from 'dlv';
import Provider from  'preact-context-provider';
import { TrackingProvider, trackingProvider, DIMENSION } from '@cube/track';
import { SerpSwitcher } from '@cube/search-input';
import { ScrollDirection } from '@cube/blocks';
import cx from 'classnames';
import UserMenu from './user-menu';
import style from './style.less';
import { Row, Column } from '@cube/layout';
import HorizontalNav from './horizontal-nav';
import { getClosest, openSearchInNewWindow, createNavigation } from '../../../lib';
import { withConfig } from '../../../config';
import configFromCurrent from '../../configfromcurrent';
import LinkEvent, { linkEventIcon } from '../../linkevent';
import { fetchSpecialTitle } from '../../../lib/scs';
import LogoIcon from '../../logoicon';

function SearchBar(props) {
	return (
		<SerpSwitcher
			inputWrapperClass={style.inputWrapper}
			context="homepage"
			compress
			providerLogoPosition="none"
			suggestionsWidthTarget={window}
			buttonClass={style.searchButton}
			openSearchInNewWindow={openSearchInNewWindow()}
			{...props}
		/>
	);
}

/**
 * MobileHeader displays a mobile-friendly fly-out menu
 * Features include customizable menu icon, configurable flyout direction, search, and home
 */
@trackingProvider({
	[DIMENSION.componentpath]: 'MobileHeader'
})
@configFromCurrent({ device: 'mobile' })
export class MobileHeader extends Component {

	state = {
		open: false,
		specialLinks: []
	}

	handleCustomLinkEvent = event => {
		if (this[event]) this[event]();
	}

	// open the menu
	openMenu = () => {
		this.setState({ open: true });

		try {
			if (this.headerRef) this.parentEl = this.parentEl || getClosest(this.headerRef, '.l-page-header');
		}
		catch (_) {/*supress: getClosest erros in tests, so this bails silently =)*/}


		if (this.parentEl) {
			this.parentEl.classList.add('hideAdhesion');
		}

		document.body.classList.add('menuOpen');
		document.body.style.top = `-${window.scrollY}px`;
		if (this.props.onMenuOpened) this.props.onMenuOpened();
	}

	// close the menu
	closeMenu = () => {
		this.setState({ open: false });

		if (this.parentEl) {
			this.parentEl.classList.remove('hideAdhesion');
		}

		document.body.classList.remove('menuOpen');
		const scrollY = document.body.style.top;
		document.body.style.top = '';
		window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
		if (this.props.onMenuClosed) this.props.onMenuClosed();
	}

	/**
	 * execute the clickEvent of the targeted area
	 *
	 * @param {Event} e the event
	 */
	runClickEventOnReturn = (e) => {
		if (e.keyCode ===  13) {
			let action = e.target._listeners.click;
			action && action();
		}
	}

	/**
	 * This function is a hack and exists to pad the top of the body
	 * to force the content down underneath the header when fixedToTop is true.
	 * This was a hack originally introduced because the header was not being
	 * instantiated as the first element on the page.
	 *
	 * Set placeholdHeightAsPadding=true to have this fire from componentDidMount.
	 * You will also want to ensure placeholdHeight=false when using it.
	 *
	 * It is not recommended to use this going forward. The Header should now be
	 * appropriately placed in the DOM instead.
	 */
	applyPlaceholderBodyPadding() {
		let t = new Date();
		let wait = setInterval(() => {
			let h = this.headerRef.clientHeight;
			if (h < 150 || new Date() - t >= 3000) {
				document.body.style.paddingTop = `${h}px`;
				clearInterval(wait);
			}
		}, 100);
	}

	handleDirectionChange = e => {
		this.setState(e);
	}

	handleOffsetForFixedToTop = pos => {
		if (this.headerRef && this.props.fixedToTop) {
			let top = this.headerRef.offsetTop;
			let dif = top - pos + (this.lastTop ? this.lastTop : 0);

			if (dif >= 0 && !this.state.waitForFixedTop) {
				this.setState({ waitForFixedTop: true });
				this.lastTop = 0;
				return;
			}
			else if (dif < 0 && this.state.waitForFixedTop) {
				this.lastTop = top;
				this.setState({ waitForFixedTop: false });
			}
		}
	}

	handleRef = ref => {
		this.headerRef = ref;
	}

	static defaultProps = {
		home: '/',
		theme: 'light',
		direction: 'right',
		allowHideNavigation: true,
		fixedToTop: true,
		placeholdHeight: true,
		placeholdHeightAsPadding: false,
		displayNavigation: true
	}

	componentWillMount () {
		this.setState({
			navigationLinks: createNavigation(this.props.navigationSections, this.props.navigationLinks)
		});

		if (this.props.showSpecial) {
			fetchSpecialTitle(this.props.streamClient, this.props.startContentServiceOrigin).then(( results ) => {
				let title = dlv(results, 'cards.0.title');
				if (title) {
					let item = {
						url: '/special/title/' + title,
						currentWhen: '^/special'
					};

					let images = dlv(results, 'cards.0.assets.0.images');

					if (images && images.length === 2) {
						item.image = {
							light: images[0].src,
							dark: images[1].src
						};
					}
					else {
						item.label = title;
					}
					this.setState({
						specialLinks: [item]
					});
				}
			});
		}
	}

	componentDidMount() {
		if (this.props.placeholdHeightAsPadding) {
			this.applyPlaceholderBodyPadding();
		}
	}

	renderPrimaryLinks(className) {
		let { primaryLinks } = this.props;

		return primaryLinks && primaryLinks.filter((link) => !link.hideWhen || !(new RegExp(link.hideWhen).test(this.props.current)), this).map((link, i) => (
			<Column class={cx(style.profile, className)}>
				<LinkEvent link={link} index={i +1} onCustomEvent={this.handleCustomLinkEvent}>
					{linkEventIcon(link, { class: style.icon })}
				</LinkEvent>
			</Column>
		));
	}

	render({ theme, navigationTheme, logo,
		fixedToTop, placeholdHeight, direction,
		primaryLabel,
		current, allowHideNavigation, displayNavigation,
		customHeader, portalStaticOrigin
	}, { open, waitForFixedTop, specialLinks, navigationLinks }) {

		fixedToTop = waitForFixedTop ? false : fixedToTop;

		let header = customHeader ?
			(
				<header ref={this.handleRef} class={cx(style.headerContainer, style.fixedToTop)}>
					{ customHeader.iframeParameters &&
						<div class={style.iframeContainer}>
							<iframe {...customHeader.iframeParameters}>
								<p>Your browser does not support iframes.</p>
							</iframe>
						</div>
					}
					<div class={cx(style.search, style.fixedSearch)}>
						<SearchBar />
					</div>
					{
						displayNavigation && (
							<TrackingProvider {...{
								[DIMENSION.componentpath]: 'NavigationLinks'
							}}
							>
								<HorizontalNav
									theme={theme}
									links={navigationLinks && navigationLinks.concat(specialLinks)}
									primaryLabel={primaryLabel}
									current={current}
									allowHideNavigation={allowHideNavigation && fixedToTop}
									scrollDirectionVertical={this.state.scrollDirectionVertical}
									initialScrollDirection={this.state.initialScrollDirection}
								/>
							</TrackingProvider>
						)
					}
				</header>
			)
			:
			(
				<header ref={this.handleRef} class={cx(style.headerContainer, fixedToTop && style.fixedToTop, open && style.open)}>
					<ScrollDirection
						debounce={1}
						onDirectionChange={this.handleDirectionChange}
						onScroll={this.handleOffsetForFixedToTop}
					/>
					<div class={cx(style.header, style[theme])} onKeyDown={this.runClickEventOnReturn}>
						<div>
							<Row class={cx(style['header-contents'], style.top, 'align-items-center')}>
								<Column class={cx(style.topCols, 'col')}>
									<LogoIcon
										theme={theme}
										size="sm"
										link={logo}
										class={style.logo}
										portalStaticOrigin={portalStaticOrigin}
									/>
								</Column>
								{this.renderPrimaryLinks(cx(style.topCols, 'col'))}
							</Row>
							<Row class={cx(style['header-contents'], style.bottom, 'align-items-center')}>
								<Column class={cx(style.search, 'col-12')}>
									<SearchBar />
								</Column>
							</Row>
						</div>
					</div>

					{
						displayNavigation && (
							<TrackingProvider {...{
								[DIMENSION.componentpath]: 'NavigationLinks'
							}}
							>
								<HorizontalNav
									theme={navigationTheme || theme}
									links={navigationLinks && navigationLinks.concat(specialLinks)}
									primaryLabel={primaryLabel}
									current={current}
									allowHideNavigation={allowHideNavigation && fixedToTop}
									scrollDirectionVertical={this.state.scrollDirectionVertical}
									initialScrollDirection={this.state.initialScrollDirection}
								/>
							</TrackingProvider>
						)
					}

					<UserMenu
						theme={theme}
						onClose={this.closeMenu}
						open={open}
						direction={direction}
					/>
				</header>
			);

		if (placeholdHeight) {
			header = (
				<div class={cx(
					style.headerPlaceholder,
					fixedToTop && style.fixedToTop,
					!displayNavigation && !customHeader && style.hideNav,
				)}
				>
					{ header }
				</div>
			);
		}

		return (<Provider theme={theme}>{header}</Provider>);
	}
}

const Header = withConfig([
	'portalStaticOrigin',

	'headerWidget.navigationTheme',

	'headerWidget.mobile.displayNavigation',
	'headerWidget.displayNavigation',

	'headerWidget.mobile.customHeader',

	'headerWidget.mobile.logo',
	'headerWidget.logo',

	'headerWidget.mobile.primaryLinks',
	'headerWidget.primaryLinks',

	'headerWidget.navigationSections',
	'headerWidget.mobile.navigationLinks',
	'headerWidget.navigationLinks',

	'headerWidget.mobile.showSpecial',
	'headerWidget.showSpecial',

	'headerWidget.mobile.fixedToTop',
	'headerWidget.fixedToTop',

	'headerWidget.mobile.placeholdHeight',
	'headerWidget.placeholdHeight',

	'headerWidget.mobile.placeholdHeightAsPadding'
])(
	(props) => <MobileHeader {...props} />
);

export default Header;
